/**
 * `/content/assignments/[id]` page logic: load, save, publish, validation report — for both template types.
 * The type picks the schema (`documentSchemaFor`) and what an issue focuses (a chapter or an
 * exercise). Saving is never gated on validation; publishing is. Every write goes through
 * `store.buildSavePayload()` (reconciles the media manifest), never `getPayload`.
 */
import { computed, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'
import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { AssessmentValidationBlocker } from '../assessments/interfaces'
import type { AssessmentValidationGroup } from '../assessments/interfaces'
import type { AssessmentValidationIssue } from '../assessments/interfaces'

import { useSidebarPrivileges } from '@entities/viewer'
import { useToast } from '@shared/ui/toast'

import { AssignmentStatus } from './enums'
import { useAssignmentEditorStore } from './store'
import { useValidateAssignmentDefinition } from './validate-definition.hook'
import { useAssignmentValidationReport } from './validation-report.hook'
import { useAssignmentTemplateShowApi } from '../../api/assignment-templates/show.hook'
import { useAssignmentTemplateUpdateApi } from '../../api/assignment-templates/update.hook'
import {
  isAssignmentTypeAuthorable,
  TEMPLATE_TITLE_MAX_LENGTH,
} from './assignment-template-options'

const LEAVE_CONFIRMATION = 'This assignment has unsaved changes. Leave and discard them?'
const TITLE_REQUIRED_MESSAGE = 'Title is required.'
const TITLE_TOO_LONG_MESSAGE = `Title must not exceed ${TEMPLATE_TITLE_MAX_LENGTH} characters.`

/** Store actions that invalidate the validation report. Row-level fields (`title`, `status`,
 *  `cover_path`, `curriculum_topic_id`) are not validated by Zod and so are not listed.
 *  `selectChapterAt` is handled apart: it edits the document only when it has to mint an id. */
const DOCUMENT_MUTATIONS = new Set<string>([
  'setDocument',
  'resetDocument',
  'reset',
  'createEmptyDocument',
  'setDisciplineCode',
  'setGrade',
  'setDefinitionTitle',
  'setDefinitionDescription',
  'addChapter',
  'removeChapter',
  'moveChapterUp',
  'moveChapterDown',
  'removeChapterAt',
  'moveChapterAt',
  'addContentBlock',
  'removeContentBlock',
  'moveContentBlockUp',
  'moveContentBlockDown',
  'updateTextBlock',
  'updateImageBlock',
  'addMediaPath',
  'addExercise',
  'removeExercise',
  'moveExerciseUp',
  'moveExerciseDown',
  'changeExerciseType',
  'updateExercise',
  'setExerciseAnswer',
  'setExerciseCorrectId',
  'addExerciseListItem',
  'removeExerciseListItem',
  'moveExerciseListItem',
  'updateExerciseListItem',
])

export interface AssignmentEditorPageReturn {
  isLoading: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  isDirty: ComputedRef<boolean>
  isNotFound: Ref<boolean>
  hasDocument: ComputedRef<boolean>
  refCanView: ComputedRef<boolean>
  refCanManage: ComputedRef<boolean>
  /** `false` for a type this editor cannot author — the page shows a placeholder instead. */
  isEditable: ComputedRef<boolean>
  /** Which editor the page mounts: chapters (READING) or exercises (MATH). */
  isMath: ComputedRef<boolean>
  issues: Ref<AssessmentValidationIssue[]>
  blockers: ComputedRef<AssessmentValidationBlocker[]>
  groups: ComputedRef<AssessmentValidationGroup[]>
  summary: ComputedRef<string | null>
  hasProblems: ComputedRef<boolean>
  hasValidated: Ref<boolean>
  /** The report describes a document that has since been edited. */
  isReportStale: ComputedRef<boolean>
  load: () => Promise<void>
  save: () => Promise<void>
  publish: () => Promise<void>
  validate: () => void
  /** `false` when the issue has no chapter or exercise to select. */
  focusIssue: (issue: AssessmentValidationIssue) => boolean
  confirmLeave: () => boolean
  /** For `onBeforeRouteUpdate`: a same-route id change discards the document like a leave does. */
  confirmRouteUpdate: (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric) => boolean
  leave: () => void
}

const toTemplateId = (raw: unknown): number | null => {
  const value = Array.isArray(raw) ? raw[0] : raw

  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return null
  }
  const id = Number(value)

  return id > 0 ? id : null
}

export const useAssignmentEditorPage = (): AssignmentEditorPageReturn => {
  const route = useRoute()
  const store = useAssignmentEditorStore()
  const toast = useToast()
  const { canViewAssignmentTemplates, canManageAssignmentTemplates } = useSidebarPrivileges()

  const isNotFound = ref<boolean>(false)

  const templateId = computed<number | null>(() => toTemplateId(route.params.id))

  const refCanView = computed<boolean>(() => canViewAssignmentTemplates.value)

  /** A type-less store is still loading, not unauthorable. */
  const isEditable = computed<boolean>(() =>
    store.getType === null || isAssignmentTypeAuthorable(store.getType))

  const isMath = computed<boolean>(() => store.isMath)

  // The header hangs Save / Validate / Publish off this, so it also carries the authorable gate.
  const refCanManage = computed<boolean>(() =>
    canManageAssignmentTemplates.value && isEditable.value)

  const {
    issues,
    hasValidated,
    validate: runValidate,
    reset: resetValidation,
  } = useValidateAssignmentDefinition(() => store.getDocument, () => store.getType)

  const {
    refBlockers: blockers,
    refGroups: groups,
    refSummary: reportSummary,
    refHasProblems: hasProblems,
    resolveTarget,
    resolveExerciseId,
    isPromptIssue,
  } = useAssignmentValidationReport(issues, () => store.getDocument)

  /** `reportSummary` is `null` for not-run, clean and blockers-only alike. */
  const summary = computed<string | null>(() => {
    if (!hasValidated.value) {
      return null
    }
    if (reportSummary.value !== null) {
      return reportSummary.value
    }

    return hasProblems.value ? null : 'No problems found'
  })

  const isReportStale = computed<boolean>(() =>
    hasValidated.value && !store.hasValidationReport)

  const isLoading = computed<boolean>(() => store.isLoading)
  const isSaving = computed<boolean>(() => store.isSaving)
  const isDirty = computed<boolean>(() => store.isDirty)
  // The store outlives the page: the create modal seeds a document before the row loads, and the
  // previous template stays seated until the show response lands. Rendering either would draw the
  // wrong editor (a MATH row under the reading layout), so wait for the route's own template.
  const hasDocument = computed<boolean>(() =>
    store.hasDocument && store.getTemplateId !== null && store.getTemplateId === templateId.value)

  /** Guards against out-of-order responses: two quick id changes must not seat the older template. */
  let loadSeq = 0

  const load = async (): Promise<void> => {
    const token = ++loadSeq

    resetValidation()

    if (templateId.value === null) {
      isNotFound.value = true
      return
    }

    isNotFound.value = false
    store.setLoading(true)

    // The show hook toasts its own failure and resolves to `null`.
    const template = await useAssignmentTemplateShowApi({ templateId: templateId.value })

    // A newer load owns the store and the loader now.
    if (token !== loadSeq) {
      return
    }

    if (!template) {
      isNotFound.value = true
      store.setLoading(false)
      return
    }

    store.loadTemplate(template)
    store.setLoading(false)
  }

  const validate = (): boolean => {
    const result = runValidate()
    store.setValidationIssues(result.issues)

    return result.valid
  }

  const focusIssue = (issue: AssessmentValidationIssue): boolean => {
    if (store.isMath) {
      const exerciseId = resolveExerciseId(issue)

      if (!exerciseId) {
        return false
      }
      store.selectExercise(exerciseId)
      if (isPromptIssue(issue)) {
        store.setPromptTab('editor')
      }

      return true
    }

    const target = resolveTarget(issue)

    if (!target?.screenId) {
      return false
    }
    store.selectChapter(target.screenId)

    return true
  }

  /** Title is a row column the Zod schema never sees. Trimmed into the store (not just the payload),
   *  or `applySavedTemplate` would report a clean template as still dirty. */
  const commitTitle = (): boolean => {
    const trimmed = store.getTitle.trim()

    if (trimmed !== store.getTitle) {
      store.setTitle(trimmed)
    }

    if (trimmed.length === 0) {
      toast.error(TITLE_REQUIRED_MESSAGE)
      return false
    }

    if (trimmed.length > TEMPLATE_TITLE_MAX_LENGTH) {
      toast.error(TITLE_TOO_LONG_MESSAGE)
      return false
    }

    return true
  }

  /** The single write path. Returns whether the row was written. */
  const commit = async (): Promise<boolean> => {
    if (!isEditable.value || !commitTitle()) {
      return false
    }

    const id = store.getTemplateId
    const payload = store.buildSavePayload()

    if (id === null || !payload) {
      return false
    }

    store.setSaving(true)
    const saved = await useAssignmentTemplateUpdateApi({ templateId: id, payload })

    if (saved) {
      store.applySavedTemplate(saved)
    }
    store.setSaving(false)

    return saved !== null
  }

  /** Validates for the report only; a failing document still saves. */
  const save = async (): Promise<void> => {
    validate()
    await commit()
  }

  /** Re-validates at click time — the API publishes anything it is sent. */
  const publish = async (): Promise<void> => {
    if (!validate()) {
      toast.error('This assignment has problems that must be fixed before publishing.')
      return
    }

    const previous = store.getStatus
    store.setStatus(AssignmentStatus.Published)

    if (!await commit()) {
      store.setStatus(previous)
    }
  }

  const leave = (): void => {
    store.clear()
    resetValidation()
  }

  const invalidateReport = (): void => {
    if (store.hasValidationReport) {
      store.clearValidationIssues()
    }
  }

  // Any document mutation closes the publish gate; the on-screen list survives, flagged stale.
  store.$onAction(({ name, after }) => {
    // Selecting a chapter is navigation, not an edit — unless the rail had to mint an id for it.
    if (name === 'selectChapterAt') {
      after((repaired) => {
        if (repaired) {
          invalidateReport()
        }
      })
      return
    }
    if (!DOCUMENT_MUTATIONS.has(name)) {
      return
    }
    after(invalidateReport)
  })

  // Vue Router reuses the component when only the id changes — re-seat the store here. The page's
  // `onBeforeRouteUpdate` has already asked about unsaved changes via `confirmRouteUpdate`.
  watch(templateId, async (next) => {
    if (next !== null && store.getTemplateId === next) {
      return
    }
    store.clear()
    await load()
  })

  const confirmLeave = (): boolean => {
    if (!store.isDirty) {
      return true
    }

    return typeof globalThis.confirm === 'function'
      ? globalThis.confirm(LEAVE_CONFIRMATION)
      : true
  }

  // A query-only update keeps the document; an id change discards it and so needs the same answer.
  const confirmRouteUpdate = (
    to: RouteLocationNormalizedGeneric,
    from: RouteLocationNormalizedGeneric,
  ): boolean => toTemplateId(to.params.id) === toTemplateId(from.params.id) || confirmLeave()

  // Tab close / reload — the router never sees these.
  useEventListener(globalThis, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!store.isDirty) {
      return
    }
    event.preventDefault()
    // Older engines need the assignment to show the prompt.
    event.returnValue = ''
  })

  return {
    isLoading,
    isSaving,
    isDirty,
    isNotFound,
    hasDocument,
    refCanView,
    refCanManage,
    isEditable,
    isMath,
    issues,
    blockers,
    groups,
    summary,
    hasProblems,
    hasValidated,
    isReportStale,
    load,
    save,
    publish,
    validate: () => { validate() },
    focusIssue,
    confirmLeave,
    confirmRouteUpdate,
    leave,
  }
}
